import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import { useCallback, useRef } from 'react';

type DebouncedFunction<T extends (...args: []) => void> = (...args: Parameters<T>) => void;

// General hook to debounce functions per unique key (id)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useDebouncedBy<T extends (...args: any[]) => void>(fn: T, delay: number = 100) {
  const debouncedFunctionsRef = useRef<
    Map<string, { args: Parameters<T>; debouncedFn: DebouncedFunction<T> }>
  >(new Map());

  // Function to trigger a debounced function per key (id)
  const debounceBy = useCallback(
    (key: string, ...args: Parameters<T>) => {
      const debouncedFunctions = debouncedFunctionsRef.current;

      // Check if a debounced function for this key already exists
      if (!debouncedFunctions.has(key)) {
        // Create and store a new debounced function in the ref
        const debouncedFn: DebouncedFunction<T> = debounce(fn, delay);
        debouncedFunctions.set(key, { args, debouncedFn });
        debouncedFn(...args);
      }

      // Call the debounced function with the provided arguments
      const debouncedFn = debouncedFunctions.get(key)!;

      if (!isEqual(debouncedFn?.args, args)) {
        debouncedFn.debouncedFn(...args);
        debouncedFunctions.set(key, { args, debouncedFn: debouncedFn.debouncedFn });
      }
    },
    [fn, delay],
  );

  return {
    debounceBy,
  };
}
